import classnames from "classnames";
import { componentWrapper } from "../../../utilities";
import { Pill } from "../../pill";
import { Text } from "../../text";
import { formatTemplateCommand } from "../template-mention.utilities";
import styles from "./template-chip.module.scss";
import type { CustomTemplateChipFactory, IProps } from "./template-chip.types";

/**
 * Display-only chip for an Ask Spotlight template: its command after a `/`. It holds no template state, so the
 * composer's node view and a sent message bubble both draw it. Its ref and any other props reach its root, so a
 * `Tooltip` can wrap it directly.
 * @param props.command   The template's command, without the `/`.
 * @param props.variant   `composer` (link-coloured text) or `message` (a `tint` pill on a sent message bubble). Defaults to `composer`.
 * @param props.selected  Whether the chip is the editor's selection. Defaults to false.
 * @param props.my        Vertical margin.
 * @param props.className Extra class on the root.
 * @returns {JSX.Element} The TemplateChip component.
 */
const TemplateChip = componentWrapper<CustomTemplateChipFactory, IProps>(
    "TemplateChip",
    ({ command, variant = "composer", selected = false, my, className, ...otherProps }, ref) => {
        const label = formatTemplateCommand(command);

        if (variant === "message") {
            return (
                <Pill
                    ref={ref as React.Ref<HTMLDivElement>}
                    variant="tint"
                    className={className}
                    my={my}
                    data-testid="template-chip"
                    {...otherProps}
                >
                    {label}
                </Pill>
            );
        }

        return (
            <Text
                ref={ref as React.Ref<HTMLParagraphElement>}
                span
                inherit
                bdrs="xs"
                className={classnames(styles["composer"], className)}
                my={my}
                data-selected={selected}
                data-testid="template-chip"
                {...otherProps}
            >
                {label}
            </Text>
        );
    }
);

export default TemplateChip;
